/**
 * Pure href/active-state resolver for the global navigation rail
 * (`components/app-rail.tsx`).
 *
 * Extracted from `useRailModel` so the "no dead link in a no-brand chrome"
 * guarantee stays a pure, directly-checkable function (apps/web has no unit
 * runner today; correctness is held by `tsc` + browser acceptance).
 *
 * Brand-selection contract (mirrors the URL-is-source-of-truth model in
 * `docs/engineering/best-practices/web-frontend-architecture.md` §4):
 *
 *   - A URL brand (`brand`) is the primary source. Its base drives every
 *     brand-scoped link and the active-state highlighting.
 *   - When the URL carries no brand (org-level surfaces like
 *     `/o/{slug}/b/new`), we fall back to the org's first workspace
 *     (`firstBrandSlug`) so Settings / Chat / etc. still land on a real
 *     page instead of a removed legacy root path.
 *   - When there is no brand at all (empty org / list still loading / no
 *     org), every brand-scoped link points at the org brand resolver
 *     (`/o/{slug}/b`, which itself routes to last-brand → first-brand →
 *     `/b/new`). A bare-root href (`/automations`, `/settings/org-info`,
 *     …) is NEVER emitted — those routes were removed in the `/o/{orgSlug}`
 *     hard cut (#1704) and 404.
 *   - Org-workspace routes (`/o/{slug}/overview`, allowlisted in
 *     `ORG_WORKSPACE_SECTIONS`) form a third scope: the rail swaps the
 *     brand menu for the org menu. Org menu hrefs come from the URL org
 *     slug on those routes (always live); off org routes they degrade
 *     through the active org, then the resolver — never a dead link.
 *   - That third scope is admin-only (`orgWorkspaceVisible`). For a plain
 *     member the org menu does not exist, so the scope never flips and the
 *     no-dead-link invariant extends to it: the rail cannot offer a link to
 *     a section that 404s for them.
 */
import {
  DEFAULT_NAV_WORKSPACE,
  type NavWorkspaceId,
  workspaceForPath,
} from '@/lib/nav-workspaces'
import { buildBrandUrl, buildOrgUrl, parseOrgPath } from '@/lib/routes'

/**
 * Org-workspace menu, shaped like the `sections` paradigm of
 * `brand-settings-nav.tsx` (section key + item slugs + i18n label keys in
 * the `Sidebar` namespace) so future org surfaces (settings, …) slot in
 * as data. Each item slug doubles as the org-route segment
 * (`/o/{slug}/{item.slug}`) and as the allowlist that flips the rail into org
 * scope — other org-level routes (e.g. `/o/{slug}/agent-brain`) keep the
 * no-brand fallback rail. Items carrying `settingsPath` are jumps into brand
 * Settings instead and take no part in the allowlist.
 */
export const ORG_WORKSPACE_SECTIONS = [
  {
    key: 'organization',
    items: [
      { slug: 'overview', labelKey: 'orgOverview' },
      { slug: 'chat', labelKey: 'orgChat' },
      { slug: 'chats', labelKey: 'orgChats' },
      { slug: 'usage', labelKey: 'orgUsage' },
      { slug: 'activity', labelKey: 'orgActivity' },
      // Billing is configuration, not supervision: the page lives under brand
      // Settings. The row stays in the org menu as a jump; `/o/{slug}/billing`
      // is only a brand-less redirect, so the slug must not flip the rail into
      // org scope (`settingsPath` items are excluded from the allowlist).
      { slug: 'billing', labelKey: 'orgBilling', settingsPath: 'plan' },
    ],
  },
] as const

const ORG_WORKSPACE_SLUGS: ReadonlySet<string> = new Set(
  ORG_WORKSPACE_SECTIONS.flatMap(section =>
    section.items
      .filter(item => !('settingsPath' in item))
      .map(item => item.slug),
  ),
)

/** Rail menu scope: brand menu (default) or the org-workspace menu. */
export type RailScope = 'brand' | 'org'

export type OrgRailItem = {
  slug: string
  /** i18n key in the `Sidebar` namespace. */
  labelKey: string
  href: string
  active: boolean
}

export type OrgRailSection = {
  key: string
  items: OrgRailItem[]
}

/**
 * Match an org-workspace pathname (`/o/{slug}/{workspaceSlug}[/...]` with an
 * allowlisted first segment) and return the URL org slug. Returns null for
 * brand routes, the `/o/{slug}` / `/o/{slug}/b` entries, and any org-level
 * route that is not part of the org-workspace menu.
 */
function matchOrgWorkspacePath(
  pathname: string | null,
): { orgSlug: string } | null {
  if (!pathname) return null
  const parsed = parseOrgPath(pathname)
  if (!parsed) return null
  const firstSegment = parsed.sub.split('/')[0] ?? ''
  return ORG_WORKSPACE_SLUGS.has(firstSegment)
    ? { orgSlug: parsed.orgSlug }
    : null
}

/**
 * Whether `pathname` sits inside the org workspace, using the same
 * `ORG_WORKSPACE_SECTIONS` allowlist that flips the rail into org scope.
 * Shared with ClientSwitcher so its "Organization workspace" entry and the
 * rail never disagree: adding a section here updates both surfaces at once.
 */
export function isOrgWorkspacePath(pathname: string | null): boolean {
  return matchOrgWorkspacePath(pathname) !== null
}

export type BuildRailHrefsInput = {
  /** URL-resolved brand, or null on a no-brand surface. */
  brand: { orgSlug: string; brandSlug: string } | null
  /** Active org slug (from ActiveScopeProvider), or null when unresolved. */
  orgSlug: string | null
  /** Alphabetically-first workspace slug, used only when `brand` is null. */
  firstBrandSlug: string | null
  pathname: string | null
  /** Last-visited chat id for the URL brand (never for the fallback brand). */
  lastChatId: string | undefined
  /**
   * Whether the caller may see the Organization Workspace at all (org admin).
   * False empties `orgSections` AND pins `scope` to `'brand'`: a member has no
   * org menu to swap in, so the rail must keep rendering the brand menu even
   * on an org-workspace pathname — which they only reach as a 404.
   */
  orgWorkspaceVisible: boolean
  /**
   * Whether the Autopilot row should be rendered: the brand-level entitlement
   * (`autopilotVisible`, admin-controlled and enforced with a 404 by every
   * autopilot endpoint) AND the user's own Labs opt-in. Both must hold — the
   * opt-in only reveals a surface the caller could already open by URL.
   */
  autopilotEnabled: boolean
  /**
   * Org-level GEO workspace Labs opt-in. False pins `navWorkspace` to `ads`
   * and hides the switcher, so the rail renders exactly as it did before the
   * workspace zone existed. Optional so existing callers stay byte-for-byte.
   */
  geoWorkspaceEnabled?: boolean
  /** The browser's remembered workspace; only consulted when enabled. */
  storedNavWorkspace?: NavWorkspaceId
  /**
   * A toolfare host: the rail lists toolfare's own pages only (see
   * `lib/connect/toolfare-console.ts`), home is the AI apps page, and there is
   * no Organization Workspace to swap in.
   */
  toolfare?: boolean
}

export type RailHrefs = {
  /** 'org' on org-workspace routes — the rail renders the org menu instead. */
  scope: RailScope
  /** Org-workspace menu with resolved hrefs + active state. */
  orgSections: OrgRailSection[]
  /** Effective brand base (URL brand, else fallback first brand, else null). */
  brandPathBase: string | null
  /** True only for a real URL brand. */
  hasUrlBrand: boolean
  /** Where the wordmark links: the brand's chat, or AI apps on toolfare. */
  homeHref: string
  chatHref: string
  dashboardHref: string
  adLauncherHref: string
  creativesHref: string
  filesHref: string
  integrationsHref: string
  marketplaceHref: string
  mcpCliHref: string
  automationsHref: string
  settingsHref: string
  /** toolfare rows: credits and billing (org admins only), and approvals. */
  showBilling: boolean
  billingHref: string
  approvalsHref: string
  isBillingActive: boolean
  isApprovalsActive: boolean
  isChatActive: boolean
  /** Also on `/campaigns`, which the Dashboard row stands for. */
  isDashboardActive: boolean
  isAdLauncherActive: boolean
  isCreativesActive: boolean
  isFilesActive: boolean
  /** Setup section rows. */
  isIntegrationsActive: boolean
  isSkillMarketplaceActive: boolean
  isMcpCliActive: boolean
  /** Render the Autopilot row (entitled + opted in, and a real URL brand). */
  showAutopilot: boolean
  autopilotHref: string
  isAutopilotActive: boolean
  isAutomationsActive: boolean
  isSettingsActive: boolean
  /** Whether the Ads / SEO & GEO switcher renders at all (Labs opt-in). */
  showNavWorkspaceSwitcher: boolean
  /**
   * Effective workspace zone: the pathname's own zone when it has one, else
   * the stored preference; always `ads` while the switcher is hidden.
   */
  navWorkspace: NavWorkspaceId
  visibilityHref: string
  citationsHref: string
  reportsHref: string
  sitesHref: string
  isVisibilityActive: boolean
  isCitationsActive: boolean
  isReportsActive: boolean
  isSitesActive: boolean
}

/**
 * Pick the no-URL-brand fallback workspace: the alphabetically-first brand by
 * display name (mirrors the server resolver `/o/{org}/b`'s first-brand
 * tiebreak). Returns null for an empty list.
 */
export function pickFallbackBrandSlug(
  brands: ReadonlyArray<{ name: string; slug: string }> | null | undefined,
): string | null {
  if (!brands || brands.length === 0) return null
  return [...brands].sort((a, b) => a.name.localeCompare(b.name))[0].slug
}

export function buildRailHrefs(input: BuildRailHrefsInput): RailHrefs {
  const { brand, orgSlug, firstBrandSlug, pathname, lastChatId } = input

  const urlBrandBase = brand
    ? buildBrandUrl(brand.orgSlug, brand.brandSlug)
    : null
  // Fall back to the org's first workspace only when the URL has no brand.
  const fallbackBase =
    !brand && orgSlug && firstBrandSlug
      ? buildBrandUrl(orgSlug, firstBrandSlug)
      : null
  const brandPathBase = urlBrandBase ?? fallbackBase
  const hasUrlBrand = urlBrandBase !== null

  // The safety net: every brand-scoped link degrades to the resolver (never
  // a removed bare-root path) when no brand base could be built.
  const orgResolverHref = orgSlug ? buildOrgUrl(orgSlug, 'b') : '/'
  const sub = (path: string): string =>
    brandPathBase
      ? `${brandPathBase}${path}`
      : orgSlug
        ? `${orgResolverHref}?next=${encodeURIComponent(path.replace(/^\//, ''))}`
        : orgResolverHref

  // Org-workspace scope: the URL org slug is authoritative on org routes.
  // Off org routes the menu is not rendered, but its hrefs still degrade
  // (active org → resolver → root) to preserve the no-dead-link invariant.
  // Non-admins have no Organization Workspace: no menu, and the scope never
  // flips, so the rail keeps its brand menu instead of collapsing to nothing.
  const orgWorkspaceVisible = input.orgWorkspaceVisible && !input.toolfare
  const orgWorkspace = orgWorkspaceVisible
    ? matchOrgWorkspacePath(pathname)
    : null
  const scope: RailScope = orgWorkspace ? 'org' : 'brand'
  const orgMenuSlug = orgWorkspace?.orgSlug ?? orgSlug
  const orgSections: OrgRailSection[] = !orgWorkspaceVisible
    ? []
    : ORG_WORKSPACE_SECTIONS.map(section => ({
        key: section.key,
        items: section.items.map(item => {
          if ('settingsPath' in item) {
            // Brand-scoped settings page; never active in org scope because
            // the rail is in brand scope whenever the user is actually there.
            return {
              slug: item.slug,
              labelKey: item.labelKey,
              href: sub(`/settings/${item.settingsPath}`),
              active: false,
            }
          }
          const href = orgMenuSlug
            ? buildOrgUrl(orgMenuSlug, item.slug)
            : orgResolverHref
          // Segment-exact active match ('/overview' + subpaths, never
          // a sibling slug that merely shares the prefix).
          const active = !!(
            orgMenuSlug &&
            pathname &&
            (pathname === href || pathname.startsWith(`${href}/`))
          )
          return { slug: item.slug, labelKey: item.labelKey, href, active }
        }),
      }))

  const isOnNewChat = !!(
    brandPathBase &&
    pathname &&
    pathname === `${brandPathBase}/chat/new`
  )
  const chatHref = brandPathBase
    ? isOnNewChat || !lastChatId
      ? `${brandPathBase}/chat/new`
      : `${brandPathBase}/chat/${lastChatId}`
    : orgResolverHref

  const isChatActive = !!(
    brandPathBase &&
    pathname &&
    (pathname === `${brandPathBase}/chat` ||
      pathname.startsWith(`${brandPathBase}/chat/`))
  )
  // Segment match: the Dashboard is `/dashboards`, and the campaign console
  // at `/campaigns` is switched to from its header; `/dashboard` only
  // redirects to one of them.
  const isDashboardActive = !!(
    brandPathBase &&
    pathname &&
    ['/dashboards', '/campaigns'].some(
      segment =>
        pathname === `${brandPathBase}${segment}` ||
        pathname.startsWith(`${brandPathBase}${segment}/`),
    )
  )
  const isAdLauncherActive = !!(
    brandPathBase &&
    pathname &&
    (pathname === `${brandPathBase}/ad-launcher` ||
      pathname.startsWith(`${brandPathBase}/ad-launcher/`))
  )
  const isFilesActive = !!(
    brandPathBase && pathname?.startsWith(`${brandPathBase}/files`)
  )
  // Covers the list page and both full-bleed canvas routes — `(canvas)` is a
  // route group, so /creatives/[id] and /creatives/template/[id] share this prefix.
  const isCreativesActive = !!(
    brandPathBase && pathname?.startsWith(`${brandPathBase}/creatives`)
  )
  const isIntegrationsActive = !!(
    brandPathBase && pathname?.startsWith(`${brandPathBase}/integrations`)
  )
  const isSkillMarketplaceActive = !!(
    brandPathBase && pathname?.startsWith(`${brandPathBase}/skills`)
  )
  // Setup → MCP & CLI: how an outside AI app reaches this workspace. Its
  // upgrade page sits under it, so a checkout keeps the row lit.
  const isMcpCliActive = !!(
    brandPathBase && pathname?.startsWith(`${brandPathBase}/mcp-cli`)
  )
  // Active-state only fires against a real brand base. The former bare-root
  // fallbacks (`startsWith('/automations')` …) referenced routes removed in
  // the `/o/{orgSlug}` hard cut and could never match a real pathname.
  const isAutopilotActive = !!(
    brandPathBase && pathname?.startsWith(`${brandPathBase}/autopilot`)
  )
  // The fallback brand is a link target, not a place the user is — never
  // advertise an experimental surface for a brand they have not opened.
  const showAutopilot = hasUrlBrand && input.autopilotEnabled
  const isAutomationsActive = !!(
    brandPathBase && pathname?.startsWith(`${brandPathBase}/automations`)
  )
  const brandActive = (segment: string): boolean =>
    !!(
      brandPathBase &&
      pathname &&
      (pathname === `${brandPathBase}/${segment}` ||
        pathname.startsWith(`${brandPathBase}/${segment}/`))
    )
  // On toolfare the billing page has its own row, so Settings stays unlit there.
  const isBillingActive = brandActive('settings/plan')
  const isApprovalsActive = brandActive('approvals')
  const isSettingsActive =
    !!(brandPathBase && pathname?.startsWith(`${brandPathBase}/settings`)) &&
    !(input.toolfare && isBillingActive)
  const mcpCliHref = sub('/mcp-cli')

  // Workspace zone. Like Autopilot, the switcher is advertised only for a
  // real URL brand; the pathname's zone beats the stored preference so a deep
  // link never shows a rail that contradicts the page beside it.
  const showNavWorkspaceSwitcher =
    hasUrlBrand && input.geoWorkspaceEnabled === true && !input.toolfare
  const navWorkspace: NavWorkspaceId = showNavWorkspaceSwitcher
    ? (workspaceForPath(pathname, brandPathBase) ??
      input.storedNavWorkspace ??
      DEFAULT_NAV_WORKSPACE)
    : DEFAULT_NAV_WORKSPACE
  const geoActive = (segment: string): boolean =>
    !!(brandPathBase && pathname?.startsWith(`${brandPathBase}/${segment}`))

  return {
    scope,
    orgSections,
    brandPathBase,
    hasUrlBrand,
    homeHref: input.toolfare ? mcpCliHref : chatHref,
    chatHref,
    dashboardHref: sub('/dashboards'),
    adLauncherHref: sub('/ad-launcher'),
    creativesHref: sub('/creatives'),
    filesHref: sub('/files'),
    integrationsHref: sub('/integrations'),
    marketplaceHref: sub('/skills'),
    mcpCliHref,
    automationsHref: sub('/automations'),
    settingsHref: sub(
      input.toolfare ? '/settings/account' : '/settings/org-info',
    ),
    // Billing is admin-only in Settings too (`brand-settings-nav.tsx`).
    showBilling: input.orgWorkspaceVisible,
    billingHref: sub('/settings/plan'),
    approvalsHref: sub('/approvals'),
    isBillingActive,
    isApprovalsActive,
    isChatActive,
    isDashboardActive,
    isAdLauncherActive,
    isCreativesActive,
    isFilesActive,
    isIntegrationsActive,
    isSkillMarketplaceActive,
    isMcpCliActive,
    showAutopilot,
    autopilotHref: sub('/autopilot'),
    isAutopilotActive,
    isAutomationsActive,
    isSettingsActive,
    showNavWorkspaceSwitcher,
    navWorkspace,
    visibilityHref: sub('/visibility'),
    citationsHref: sub('/citations'),
    reportsHref: sub('/reports'),
    sitesHref: sub('/sites'),
    isVisibilityActive: geoActive('visibility'),
    isCitationsActive: geoActive('citations'),
    isReportsActive: geoActive('reports'),
    isSitesActive: geoActive('sites'),
  }
}
